import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'components/tooltip';

const defaultArray = [];
const removedTriggerMarker = "(trigger since removed)";
const driverTriggerMarker = "(set for this employee)";
const removedDriverTriggerMarker = "(set for this employee, since removed)";

const triggerShape = PropTypes.shape({
    reviewTypeTriggerId: PropTypes.number,
    employeeReviewTriggerId: PropTypes.number,
    triggerKey: PropTypes.string,
    description: PropTypes.string,
    metDate: PropTypes.string,
    removed: PropTypes.bool
});

// Whether a review was raised by triggers that can be named, so every surface offering the
// trigger list agrees on when to show it.
export function hasRaisingTriggers(raisedBy, triggers) {
    return raisedBy === "Trigger" && triggers != null && triggers.length > 0;
}

// Lists the triggers that raised a review, led by a sentence that starts with leadIn. A trigger
// that has since been taken off the review type is still listed, marked as removed, and a trigger
// set for the driver alone is marked as theirs. A trigger with no description is named by its key
// so the bullet is never blank. The API's order is the order listed.
export function TriggersMet(
    {
        triggers = defaultArray,
        leadIn
    }
) {
    let heading = `${leadIn} these triggers were met:`;

    if (triggers.length === 1) {
        heading = `${leadIn} this trigger was met:`;
    }

    return (
        <Fragment>
            {heading}
            <ul>
                {triggers.map((trigger) => {
                    let description = trigger.description;

                    if (description == null || description === "") {
                        description = trigger.triggerKey;
                    }

                    const driverTrigger = trigger.employeeReviewTriggerId != null;
                    let marker = null;
                    let key = `type-${trigger.reviewTypeTriggerId}`;

                    if (driverTrigger) {
                        marker = trigger.removed ? removedDriverTriggerMarker : driverTriggerMarker;
                        key = `employee-${trigger.employeeReviewTriggerId}`;
                    } else if (trigger.removed) {
                        marker = removedTriggerMarker;
                    }

                    if (marker != null) {
                        description = `${description} ${marker}`;
                    }

                    return <li key={key}>{description}</li>;
                })}
            </ul>
        </Fragment>
    );
}

TriggersMet.propTypes = {
    triggers: PropTypes.arrayOf(triggerShape),
    leadIn: PropTypes.string.isRequired
};

// The Raised By value for a review, with a tooltip naming the triggers that raised it when it was raised by any.
export function RaisedByDisplay(
    {
        raisedBy = null,
        triggers = defaultArray
    }
) {
    if (!hasRaisingTriggers(raisedBy, triggers)) {
        return raisedBy;
    }

    return (
        <Tooltip
            content={(
                <div className="include-tooltip-bullet-points">
                    <TriggersMet triggers={triggers} leadIn="Raised because" />
                </div>
            )}
        >
            <span>{raisedBy}</span>
        </Tooltip>
    );
}

RaisedByDisplay.propTypes = {
    raisedBy: PropTypes.string,
    triggers: PropTypes.arrayOf(triggerShape)
};
